import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Landmark, ShoppingCart, Truck, Scale, Package, ShieldCheck, Sparkles, Search, ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import BotonAyuda from '../components/BotonAyuda';

// ============================================================
// Página índice de Reportes (rediseño, parte 5): todos los reportes en
// tarjetas por tema, con un buscador. Solo se muestran los que el usuario
// puede abrir (mismo rol, sección y módulo que la ruta en App.jsx); los que
// están por desarrollar se ven en gris con "pronto". Cada reporte es siempre
// de la empresa activa: nunca se consolidan empresas.
// ============================================================

const JERARQUIA = ['consultor', 'operador', 'contador', 'gerente', 'admin'];

const TINTES = {
  turquesa: 'bg-turquoise-50 text-turquoise-600',
  azul: 'bg-blue-50 text-blue-700',
  naranja: 'bg-orange-50 text-orange-700',
  violeta: 'bg-violet-50 text-violet-700',
  verde: 'bg-green-50 text-green-700',
  gris: 'bg-slate-100 text-slate-700',
  rosa: 'bg-rose-50 text-rose-700'
};

const r = (label, to, req = {}) => ({ label, to, req });
const pronto = (label) => ({ label, pronto: true });

export const TEMAS = [
  {
    titulo: 'Tesorería', icon: Landmark, tinte: 'turquesa',
    reportes: [
      r('Resumen de Bancos', '/bancos/resumen', { seccion: 'dashboard' }),
      r('Flujo de caja', '/reportes/flujo-caja'),
      r('Flujo proyectado', '/reportes/flujo-proyectado'),
      pronto('Estado de cuenta por banco'),
      r('Movimientos duplicados o inusuales', '/reportes/movimientos-inusuales', { rol: 'contador', seccion: 'movimientos' })
    ]
  },
  {
    titulo: 'Ventas', icon: ShoppingCart, tinte: 'azul',
    reportes: [
      r('Dashboard de ventas', '/ventas', { rol: 'operador', modulo: 'ventas' }),
      r('Antigüedad de cuentas por cobrar', '/reportes/antiguedad-por-cobrar', { rol: 'contador', modulo: 'reporte_cxc' }),
      r('Resumen del día de caja', '/caja/resumen', { rol: 'operador', modulo: 'caja' }),
      r('Cierre fiscal diario', '/reportes/cierre-fiscal-diario', { rol: 'operador', modulo: 'caja' }),
      r('Libro de ventas', '/reportes/libro-ventas', { rol: 'contador', modulo: 'reporte_libro_fiscal' })
    ]
  },
  {
    titulo: 'Compras', icon: Truck, tinte: 'naranja',
    reportes: [
      r('Antigüedad de cuentas por pagar', '/reportes/antiguedad-por-pagar', { rol: 'contador', modulo: 'reporte_cxp' }),
      r('Comparador de precios', '/proveedores/comparador-precios', { modulo: 'proveedores' }),
      r('Libro de compras', '/reportes/libro-compras', { rol: 'contador', modulo: 'reporte_libro_fiscal' }),
      r('Retenciones', '/reportes/retenciones', { rol: 'contador', modulo: 'reporte_libro_fiscal' }),
      pronto('Análisis de compras')
    ]
  },
  {
    titulo: 'Resultados', icon: Scale, tinte: 'violeta',
    reportes: [
      r('Estado de resultados', '/reportes/estado-resultados', { rol: 'contador', seccion: 'movimientos' }),
      r('Balance general', '/reportes/balance-general', { rol: 'contador', seccion: 'movimientos' }),
      r('Egresos por categoría', '/reportes/egresos-categoria'),
      pronto('Presupuesto vs. real'),
      pronto('Costos fijos vs. variables'),
      r('Cierre mensual o anual', '/reportes/cierre', { rol: 'contador', seccion: 'movimientos' })
    ]
  },
  {
    titulo: 'Inventario y producción', icon: Package, tinte: 'verde',
    reportes: [
      r('Inventario actual', '/inventario/actual', { rol: 'operador', modulo: 'productos' }),
      r('Lista de precios', '/inventario/lista-precios', { rol: 'operador', modulo: 'productos' }),
      r('Costo real de actividades', '/reportes/costo-actividades', { rol: 'operador', modulo: 'productos' }),
      r('Estimaciones de producción', '/produccion/estimaciones', { seccion: 'produccion', modulo: 'produccion_extrusion' }),
      pronto('Eficiencia de producción')
    ]
  },
  {
    titulo: 'Control', icon: ShieldCheck, tinte: 'gris',
    reportes: [
      r('Auditoría', '/auditoria', { rol: 'gerente', seccion: 'auditoria' }),
      r('Actividad por usuario', '/reportes/actividad-usuario', { rol: 'gerente', seccion: 'auditoria' })
    ]
  },
  {
    titulo: 'Personalizados', icon: Sparkles, tinte: 'rosa',
    reportes: [pronto('Armar mi propio reporte')]
  }
];

const norm = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function Reportes() {
  const { empresaActivaNombre, rolActual, esAdminGlobal, seccionesDeshabilitadas, modulosActivos } = useAuth();
  const rol = esAdminGlobal ? 'admin' : rolActual || 'consultor';
  const [busqueda, setBusqueda] = useState('');

  const puede = (req) =>
    (!req.rol || JERARQUIA.indexOf(rol) >= JERARQUIA.indexOf(req.rol)) &&
    (!req.seccion || !seccionesDeshabilitadas.includes(req.seccion)) &&
    (!req.modulo || !!modulosActivos?.[req.modulo]);

  const temas = useMemo(() => {
    const q = norm(busqueda.trim());
    return TEMAS.map((t) => ({
      ...t,
      reportes: t.reportes
        .filter((x) => x.pronto || puede(x.req))
        .filter((x) => !q || norm(x.label).includes(q) || norm(t.titulo).includes(q))
    })).filter((t) => t.reportes.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, rol, seccionesDeshabilitadas, modulosActivos]);

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Reportes
          {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="relative sm:w-72">
          <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar reporte"
            aria-label="Buscar reporte"
            className="w-full border rounded-md pl-8 pr-3 py-2 text-sm bg-white"
          />
        </div>
      </div>

      {temas.length === 0 ? (
        <p className="text-sm text-slate-500">Ningún reporte coincide con la búsqueda.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
          {temas.map((t) => {
            const Icono = t.icon;
            return (
              <section key={t.titulo} className="bg-white rounded-xl border border-slate-200 p-4">
                <h2 className="flex items-center gap-2.5 text-sm font-bold text-slate-800 mb-2">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${TINTES[t.tinte]}`}>
                    <Icono size={17} />
                  </span>
                  {t.titulo}
                </h2>
                <ul className="divide-y divide-slate-100">
                  {t.reportes.map((x) => (
                    <li key={x.label}>
                      {x.pronto ? (
                        <span className="flex items-center justify-between gap-2 px-1 py-2.5 text-sm text-slate-400">
                          {x.label}
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">pronto</span>
                        </span>
                      ) : (
                        <Link to={x.to} className="group flex items-center justify-between gap-2 rounded-md px-1 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-primary-700">
                          {x.label}
                          <ChevronRight size={16} className="text-slate-400 group-hover:text-primary-600" />
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
      <p className="text-xs text-slate-500">Todos los reportes son de la empresa activa.</p>
    </div>
  );
}
